import { useState } from 'react';
import translations from '../i18n/translations';
import GdprPage from './GdprPage';

const API = process.env.REACT_APP_API_URL || '/api';
const LANGS = ['cs', 'sk', 'ro', 'en', 'hu'];

const inputStyle = {
  width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb',
  borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#fff',
};

const COUNTRIES = [
  { code: 'CZ', name: { cs: 'Česká republika', sk: 'Česká republika', ro: 'Republica Cehă', en: 'Czech Republic', hu: 'Csehország' } },
  { code: 'SK', name: { cs: 'Slovensko', sk: 'Slovensko', ro: 'Slovacia', en: 'Slovakia', hu: 'Szlovákia' } },
  { code: 'RO', name: { cs: 'Rumunsko', sk: 'Rumunsko', ro: 'România', en: 'Romania', hu: 'Románia' } },
  { code: 'HU', name: { cs: 'Maďarsko', sk: 'Maďarsko', ro: 'Ungaria', en: 'Hungary', hu: 'Magyarország' } },
  { code: 'PL', name: { cs: 'Polsko', sk: 'Poľsko', ro: 'Polonia', en: 'Poland', hu: 'Lengyelország' } },
  { code: 'DE', name: { cs: 'Německo', sk: 'Nemecko', ro: 'Germania', en: 'Germany', hu: 'Németország' } },
  { code: 'AT', name: { cs: 'Rakousko', sk: 'Rakúsko', ro: 'Austria', en: 'Austria', hu: 'Ausztria' } },
  { code: 'OTHER', name: { cs: 'Jiný', sk: 'Iný', ro: 'Altul', en: 'Other', hu: 'Egyéb' } },
];

// Field must stay OUTSIDE RegisterPage – defined inside, React remounts the <input> on every render and focus is lost.
const Field = ({ label, fkey, required, type = 'text', form, set }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
      {label} {required && <span style={{ color: '#0e76bc' }}>*</span>}
    </label>
    <input type={type} value={form[fkey] ?? ''} onChange={e => set(fkey, e.target.value)} style={inputStyle} />
  </div>
);

export default function RegisterPage({ onBack }) {
  const [lang, setLang] = useState('cs');
  const tr = translations[lang] || translations.cs;
  const t = (key) => tr[key] || translations.cs[key] || key;

  const [form, setForm] = useState({
    company_name: '', company_ico: '', vat_number: '', country: 'CZ',
    address: '', city: '', zip: '',
    title: '', first_name: '', last_name: '', email: '', phone: '', password: '', password2: '',
    gdpr_consent: false, interest_news: false, interest_training: false,
  });
  const [loading, setLoading] = useState(false);
  const [showGdpr, setShowGdpr] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.gdpr_consent) { setError(t('gdprConsent')); return; }
    if (form.password !== form.password2) { setError(t('password') + ' - ' + (lang === 'cs' ? 'hesla se neshodují' : 'passwords do not match')); return; }
    if (form.password.length < 6) { setError(lang === 'cs' ? 'Heslo musí mít alespoň 6 znaků' : 'Password must be at least 6 characters'); return; }

    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lang }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      setSuccess(true);
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
  };


  if (showGdpr) return <GdprPage onClose={() => setShowGdpr(false)} />;

  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0e76bc 0%, #0b5e96 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'Outfit','Segoe UI',sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
          <h2 style={{ margin: '0 0 12px', color: '#111827' }}>{t('registerSuccess')}</h2>
          <button onClick={onBack} style={{ marginTop: 20, background: 'linear-gradient(135deg, #0e76bc, #0b5e96)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {t('login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', fontFamily: "'Outfit','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');`}</style>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0e76bc 0%, #0b5e96 100%)', padding: '24px 20px', textAlign: 'center' }}>
        <img src="/Grandstreampartner.png" alt="Grandstream" style={{ height: 60, marginBottom: 12 }} />
        <h1 style={{ margin: 0, color: '#fff', fontSize: 22, fontWeight: 800 }}>{t('registerTitle')}</h1>
        <p style={{ margin: '6px 0 16px', color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>{t('registerSubtitle')}</p>
        {/* Lang switcher */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          {LANGS.map(l => (
            <button key={l} onClick={() => setLang(l)} style={{ padding: '3px 10px', border: '1px solid', borderColor: lang === l ? '#fff' : 'rgba(255,255,255,0.4)', borderRadius: 5, background: lang === l ? '#fff' : 'transparent', color: lang === l ? '#0e76bc' : '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 16px' }}>

        {/* Company info */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px', marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <h2 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#0e76bc', display: 'flex', alignItems: 'center', gap: 8 }}>🏢 {t('companyInfo')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
            <div style={{ gridColumn: '1 / -1' }}><Field form={form} set={set} label={t('companyName')} fkey="company_name" required /></div>
            <Field form={form} set={set} label={t('companyIco')} fkey="company_ico" required />
            <Field form={form} set={set} label={t('vatNumber')} fkey="vat_number" />
            <div style={{ gridColumn: '1 / -1' }}><Field form={form} set={set} label={t('addressLabel')} fkey="address" required /></div>
            <Field form={form} set={set} label={t('cityLabel')} fkey="city" required />
            <Field form={form} set={set} label={t('zipLabel')} fkey="zip" required />
            <div style={{ gridColumn: '1 / -1', marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{t('stateLabel')} <span style={{ color: '#0e76bc' }}>*</span></label>
              <select value={form.country} onChange={e => set('country', e.target.value)} style={{ ...inputStyle }}>
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name[lang] || c.name.en}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* User info */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px', marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <h2 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#0e76bc', display: 'flex', alignItems: 'center', gap: 8 }}>👤 {t('userInfo')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
            <Field form={form} set={set} label={t('titleLabel')} fkey="title" />
            <div />
            <Field form={form} set={set} label={t('firstName')} fkey="first_name" required />
            <Field form={form} set={set} label={t('lastName')} fkey="last_name" required />
            <Field form={form} set={set} label={t('email')} fkey="email" required type="email" />
            <Field form={form} set={set} label={t('phoneLabel')} fkey="phone" required />
            <Field form={form} set={set} label={t('password')} fkey="password" required type="password" />
            <Field form={form} set={set} label={lang === 'cs' ? 'Heslo znovu' : lang === 'sk' ? 'Heslo znova' : lang === 'hu' ? 'Jelszó újra' : 'Repeat password'} fkey="password2" required type="password" />
          </div>
        </div>

        {/* Consents */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px', marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.gdpr_consent} onChange={e => set('gdpr_consent', e.target.checked)} style={{ marginTop: 3, flexShrink: 0, width: 18, height: 18, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                <span style={{ color: '#0e76bc', fontWeight: 700 }}>* </span>{t('gdprConsent')}{' '}
                <span onClick={() => setShowGdpr(true)} style={{ color: '#0e76bc', cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}>
                  {lang === 'cs' ? 'Zobrazit podmínky' : lang === 'sk' ? 'Zobraziť podmienky' : lang === 'hu' ? 'Feltételek megtekintése' : 'View conditions'}
                </span>
              </span>
            </label>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.interest_news} onChange={e => set('interest_news', e.target.checked)} style={{ marginTop: 3, flexShrink: 0, width: 18, height: 18, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{t('interestNews')}</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.interest_training} onChange={e => set('interest_training', e.target.checked)} style={{ marginTop: 3, flexShrink: 0, width: 18, height: 18, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{t('interestTraining')}</span>
            </label>
          </div>
        </div>

        {error && <div style={{ background: '#e8f4fc', border: '1px solid #b3d9f2', color: '#dc2626', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={handleSubmit} disabled={loading} style={{ flex: 1, background: 'linear-gradient(135deg, #0e76bc, #0b5e96)', color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1 }}>
            {loading ? '...' : t('register')}
          </button>
          <button onClick={onBack} style={{ background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            {t('back')}
          </button>
        </div>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#9ca3af' }}>
          {t('alreadyRegistered')} <span onClick={onBack} style={{ color: '#0e76bc', cursor: 'pointer', fontWeight: 600 }}>{t('login')}</span>
        </p>
      </div>
    </div>
  );
}
